import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import StarIcon from '@mui/icons-material/Star';

const LADDER_COLLAPSED_KEY = 'offerLadderCollapsed';

// Assessed-value rungs. The first offer is the deepest cut, so 1st sits at or below 2nd, and 2nd at or below Ceiling.
const LADDER_FIELDS = [
  { key: 'open', field: 'AlgorithmFirstOffer', label: '1st', hint: 'Suggested first offer level.' },
  { key: 'mid', field: 'AlgorithmSecondOffer', label: '2nd', hint: 'Suggested counter level.' },
  { key: 'walk', field: 'AlgorithmCeilingOffer', label: 'Ceiling', hint: 'The ceiling offer we could take to avoid trials.' },
];

/* ---------------------------------------------------------------- scale tuning */

// The track runs from (Start reduction + left pad) to (Walk reduction − right pad).
// Pads scale with the ladder's spread so Walk lands around 75–80% of the track even at the
// 3% floor, which keeps room for the hatched "past walk" zone.
const LEFT_PAD = { min: 5, frac: 0.15 };
const RIGHT_PAD = { min: 7, frac: 0.28 };
// Locked settlements are stored as assessed value. Converting back to market value can miss
// the rung they were struck at, so ±$1,000 still counts as that rung.
const SETTLED_MV_BAND = 1000;

/* ---------------------------------------------------------------- palette */

// Quiet palette: green for the planned range, sand for concessions, muted rose past Walk.
// `frame` must match the border of the box the rail docks onto.
// `settled` is text and borders only — settled chips stay transparent.
export const LADDER_THEME = {
  frame: '#cfd5dd',
  surface: '#ffffff',
  text: '#121826',
  textMuted: '#5a6372',
  settled: '#b7bec8',
  tick: '#98a2b3',
  zoneBeyond: '#e9eef4', // deeper than our opening offer
  zoneTarget: '#a8d0b6', // Start → Mid
  zoneConcede: '#ead9b5', // Mid → Walk
  zonePast: 'repeating-linear-gradient(135deg, #e9b7b0 0 3px, #f7e1de 3px 6px)', // past Walk
  chipBorder: '#cfd5dd',
  activeBg: '#e8eef6',
  activeBorder: '#6f8fb8',
  activeText: '#1d3a5f',
  walkBorder: '#e2b4ad',
  walkText: '#9b2c22',
  ourMarker: '#1d3a5f',
  townMarker: '#3a4352',
  danger: '#b42318',
  dotBeyond: '#7b8798',
  dotTarget: '#1f7a45',
  dotConcede: '#8a6232',
};

const CHEVRON_SPACE = 22; // px kept clear at the right of the chip row for the collapse button
const CHIP_GAP = 4; // min px between chips
const CHAR_PX = 6.15; // rough width per character at 11px bold

/* ----------------------------------------------------------------- data */

export function getLadderFromCase(caseObj) {
  if (!caseObj) return null;
  const rungs = LADDER_FIELDS.map(({ key, field, label, hint }) => {
    const av = parseNum(caseObj[field]);
    return av != null && av > 0 ? { key, label, hint, av } : null;
  });
  if (rungs.some((rung) => !rung)) return null;
  // Each rung must give back value. Equal neighbours are a real ladder (case strength 100).
  const ascending = rungs.every((rung, i) => i === 0 || rungs[i - 1].av <= rung.av);
  if (!ascending) return null;
  return rungs;
}

export function useOfferLadderCollapsed() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(LADDER_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(LADDER_COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        // Storage can be unavailable; the in-memory toggle still applies.
      }
      return next;
    });
  }, []);
  return [collapsed, toggle];
}

/* ------------------------------------------------ pure helpers (exported for tests) */

export function parseNum(value) {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : parseFloat(String(value).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

/** Reduction in % points from current market value: 25 means "-25%". */
export const reductionPct = (mv, ifmv) => ((ifmv - mv) / ifmv) * 100;

/** Returns r → position on the track (0 = left, 1 = right) for a reduction r. */
export function buildScale(rStart, rWalk) {
  const spread = Math.max(rStart - rWalk, 0);
  const lo = rStart + Math.max(LEFT_PAD.min, spread * LEFT_PAD.frac);
  const hi = rWalk - Math.max(RIGHT_PAD.min, spread * RIGHT_PAD.frac);
  const span = lo - hi;
  return (r) => Math.min(1, Math.max(0, (lo - r) / span));
}

export const formatCompactMv = (mv, decimals = 2) =>
  mv >= 1e6 ? `$${(mv / 1e6).toFixed(decimals)}M` : `$${Math.round(mv / 1e3)}K`;

export const formatCompactAv = (av, decimals = 2) => {
  const n = Math.abs(av);
  if (n >= 1e6) return `${(av / 1e6).toFixed(decimals)}M`;
  if (n >= 1e3) return `${Math.round(av / 1e3)}K`;
  return `${Math.round(av)}`;
};

const estimateChipWidth = (text, hasIcon) => text.length * CHAR_PX + 17 + (hasIcon ? 15 : 0);

/**
 * Places chip centers (px) inside [0, usable] without overlapping.
 * The 2nd chip stays on its tick. 1st slides left and Ceiling slides right
 * just far enough to clear it. If they cannot fit, they keep a gap and may extend past the row.
 */
export function spreadChips(xs, widths, usable, gap = CHIP_GAP) {
  const n = xs.length;
  const half = widths.map((w) => w / 2);
  const totalWidth = widths.reduce((a, b) => a + b, 0);

  if (n > 1 && totalWidth + gap * (n - 1) > usable) {
    const extra = (usable - totalWidth) / (n - 1);
    const out = [];
    let cursor = 0;
    for (let i = 0; i < n; i += 1) {
      out.push(cursor + half[i]);
      cursor += widths[i] + extra;
    }
    return out;
  }

  if (n === 3) {
    const clamp = (x, i) => Math.min(Math.max(x, half[i]), Math.max(half[i], usable - half[i]));
    let mid = clamp(xs[1], 1);
    let first = Math.min(xs[0], mid - half[1] - gap - half[0]);
    let last = Math.max(xs[2], mid + half[1] + gap + half[2]);
    // 1st is against the left edge: shift 2nd (and Ceiling, if needed) right.
    if (first < half[0]) {
      first = half[0];
      mid = Math.max(mid, first + half[0] + gap + half[1]);
      last = Math.max(last, mid + half[1] + gap + half[2]);
    }
    // Ceiling is against the right edge: shift 2nd (and 1st, if needed) left.
    if (last > usable - half[2]) {
      last = usable - half[2];
      mid = Math.min(mid, last - half[2] - gap - half[1]);
      first = Math.min(first, mid - half[1] - gap - half[0]);
      if (first < half[0]) first = half[0];
    }
    return [first, mid, last];
  }

  const out = xs.map((x, i) => Math.min(Math.max(x, half[i]), usable - half[i]));
  for (let i = 1; i < n; i += 1) {
    out[i] = Math.max(out[i], out[i - 1] + half[i - 1] + half[i] + gap);
  }
  for (let i = n - 1; i >= 0; i -= 1) {
    const max = i === n - 1 ? usable - half[i] : out[i + 1] - half[i + 1] - half[i] - gap;
    out[i] = Math.min(out[i], max);
  }
  return out;
}

/** Keeps every rung's amount. Close chips use 1 decimal and slide outward to clear the 2nd chip. */
export function layoutChips(rungs, rowWidth, isAVMode = false) {
  const usable = rowWidth - CHEVRON_SPACE;
  if (!(usable > 0)) return null; // not measured yet: render at % positions
  const ticks = rungs.map((rung) => rung.p * rowWidth);
  const amountText = (rung, decimals) => (
    isAVMode ? formatCompactAv(rung.av, decimals) : formatCompactMv(rung.mv, decimals)
  );
  const widthsFor = (decimals) => rungs.map((rung) => (
    estimateChipWidth(`${rung.label} ${amountText(rung, decimals)}`, rung.isWalk)
  ));
  const full = widthsFor(2);
  const centers = ticks.map((tick, i) => {
    const h = full[i] / 2;
    return Math.min(Math.max(tick, h), Math.max(h, usable - h));
  });
  const crowded = centers.some((center, i) => (
    i > 0 && center - full[i] / 2 < centers[i - 1] + full[i - 1] / 2 + CHIP_GAP
  ));
  const decimals = crowded ? 1 : 2;
  return { decimals, xs: spreadChips(ticks, decimals === 2 ? full : widthsFor(1), usable) };
}

const pct = (p) => `${(p * 100).toFixed(2)}%`;
const fmtMoney = (n) => `$${(Math.round(n / 10) * 10).toLocaleString()}`;
const fmtPct = (r) => `${r >= 0 ? '-' : '+'}${Math.abs(Math.round(r))}%`;

function snapLockedMarker(marker, rungs) {
  if (!marker) return null;
  let nearest = null;
  let nearestDist = Infinity;
  for (const rung of rungs) {
    const dist = Math.abs(marker.mv - rung.mv);
    if (dist <= SETTLED_MV_BAND && dist < nearestDist) {
      nearest = rung;
      nearestDist = dist;
    }
  }
  if (!nearest) return marker;
  // Equal rungs share one tick, so the ceiling check paints the band as past it.
  // Land on the first rung, which is the green level.
  const sameLevel = rungs.every((rung) => Math.abs(rung.mv - rungs[0].mv) <= SETTLED_MV_BAND);
  const rung = sameLevel ? rungs[0] : nearest;
  return { ...marker, p: rung.p, pastWalk: false, snappedKey: rung.key };
}

/* ------------------------------------------------------------- component */

export default function OfferLadderRail({
  ladderAV,
  ifmv,
  conversionFactor,
  ourOfferAV,
  townOfferAV,
  onSelect,
  disabled = false,
  collapsed,
  onToggleCollapsed,
  isAVMode = false,
  zIndex = 100,
  theme, // optional partial override of LADDER_THEME
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [rowEl, setRowEl] = useState(null);
  const [rowWidth, setRowWidth] = useState(0);
  const t = useMemo(() => ({ ...LADDER_THEME, ...theme }), [theme]);

  const model = useMemo(() => {
    const cf = parseNum(conversionFactor);
    const base = parseNum(ifmv);
    if (!Array.isArray(ladderAV) || ladderAV.length < 2 || !(cf > 0) || !(base > 0)) return null;

    const last = ladderAV.length - 1;
    const rungs = ladderAV.map((rung, i) => {
      const mv = Math.round(rung.av / cf / 10) * 10; // same rounding as the toolbar inputs
      return { ...rung, mv, r: reductionPct(mv, base), isWalk: i === last };
    });
    const walk = rungs[last];
    const scale = buildScale(rungs[0].r, walk.r);

    const marker = (raw) => {
      const av = parseNum(raw);
      if (av == null || av <= 0) return null;
      const mv = av / cf;
      const r = reductionPct(mv, base);
      return { mv, r, p: scale(r), pastWalk: mv > walk.mv + 1 };
    };

    return {
      rungs: rungs.map((rung) => ({ ...rung, p: scale(rung.r) })),
      our: marker(ourOfferAV),
      town: marker(townOfferAV),
    };
  }, [ladderAV, ifmv, conversionFactor, ourOfferAV, townOfferAV]);

  // Measure the chip row so chips can be placed in px and de-collided.
  useEffect(() => {
    if (!rowEl) return undefined;
    setRowWidth(rowEl.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => setRowWidth(entry.contentRect.width));
    ro.observe(rowEl);
    return () => ro.disconnect();
  }, [rowEl]);

  const chipLayout = useMemo(
    () => (model ? layoutChips(model.rungs, rowWidth, isAVMode) : null),
    [model, rowWidth, isAVMode]
  );

  const modifiers = useMemo(() => [
    { name: 'offset', options: { offset: [0, -1] } },
    { name: 'flip', enabled: false },
    { name: 'preventOverflow', enabled: false },
    { name: 'computeStyles', options: { gpuAcceleration: false, adaptive: false, roundOffsets: false } },
    {
      name: 'pinLadder',
      enabled: !collapsed,
      phase: 'main',
      requires: ['offset'],
      fn({ state }) {
        if (state.modifiersData.popperOffsets) state.modifiersData.popperOffsets.x = state.rects.reference.x;
      },
    },
    {
      name: 'matchAnchorWidth',
      enabled: !collapsed,
      phase: 'beforeWrite',
      requires: ['computeStyles'],
      fn({ state }) {
        state.styles.popper.width = `${state.rects.reference.width}px`;
      },
      effect({ state }) {
        const popper = state.elements.popper;
        popper.style.width = `${state.elements.reference.getBoundingClientRect().width}px`;
        return () => {
          popper.style.width = '';
        };
      },
    },
  ], [collapsed]);

  if (!model) return null;

  const { rungs } = model;
  const our = disabled ? snapLockedMarker(model.our, rungs) : model.our;
  const town = disabled ? snapLockedMarker(model.town, rungs) : model.town;
  const decimals = chipLayout?.decimals ?? 2;
  const frameStyle = {
    borderColor: t.frame,
    background: t.surface,
    color: disabled ? t.settled : t.text,
  };

  const outperformed = !!our && our.p < rungs[0].p;
  const ourDotColor = (() => {
    if (!our) return t.ourMarker;
    if (!disabled) return our.pastWalk ? t.danger : t.ourMarker;
    if (our.snappedKey === rungs[0].key) return t.dotTarget;
    if (our.pastWalk) return t.danger;
    if (outperformed) return t.dotBeyond;
    for (let i = 1; i < rungs.length; i += 1) {
      if (our.p < rungs[i].p || i === rungs.length - 1) return i === 1 ? t.dotTarget : t.dotConcede;
    }
    return t.danger;
  })();

  // Zone widths: beyond Start, then one segment per rung gap, then past Walk (flex: 1).
  const zones = [{ w: rungs[0].p, color: t.zoneBeyond }];
  for (let i = 1; i < rungs.length; i += 1) {
    zones.push({ w: rungs[i].p - rungs[i - 1].p, color: i === 1 ? t.zoneTarget : t.zoneConcede });
  }

  const chipStyle = (isOurs, isWalk) => {
    if (disabled) return { background: 'transparent', borderColor: t.settled, color: t.settled };
    if (isOurs) return { background: t.activeBg, borderColor: t.activeBorder, color: t.activeText };
    if (isWalk) return { background: t.surface, borderColor: t.walkBorder, color: t.walkText };
    return { background: t.surface, borderColor: t.chipBorder, color: t.text };
  };

  return (
    <>
      {/* Cover the toolbar border box so the rail's side borders share its left and right edges. */}
      <span ref={setAnchorEl} className="pointer-events-none absolute -inset-px" />
      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement={collapsed ? 'top-end' : 'top'}
        modifiers={modifiers}
        style={{ zIndex }}
      >
        {collapsed ? (
          <button
            type="button"
            onClick={onToggleCollapsed}
            aria-expanded={false}
            aria-label="Show suggested offers"
            className="flex items-center gap-0.5 whitespace-nowrap rounded-t border border-b-0 px-1.5 text-[11px] font-bold leading-none"
            style={{ ...frameStyle, height: 18 }}
          >
            Suggested Offers
            <ExpandLessIcon sx={{ fontSize: 14 }} />
          </button>
        ) : (
          <div
            role="group"
            aria-label="Suggested offers"
            className="box-border w-full rounded-t-md border border-b-0 px-3 pb-[7px] pt-[6px]"
            style={frameStyle}
          >
            {/* Chip row */}
            <div ref={setRowEl} className="relative h-[21px]">
              {rungs.map((rung, i) => {
                const isOurs = !!our && (our.snappedKey ? our.snappedKey === rung.key : Math.abs(our.mv - rung.mv) <= 10);
                const left = chipLayout ? `${chipLayout.xs[i]}px` : pct(rung.p);
                const style = chipStyle(isOurs, rung.isWalk);
                const chipAmount = isAVMode ? formatCompactAv(rung.av, decimals) : formatCompactMv(rung.mv, decimals);

                return (
                  <Tooltip
                    key={rung.key}
                    arrow
                    placement="top"
                    disableInteractive
                    title={
                      <div className="text-[12px] leading-[16px]">
                        <div className="font-bold">{rung.label}</div>
                        <div>{rung.hint}</div>
                        <div>{isAVMode ? Math.round(rung.av).toLocaleString() : fmtMoney(rung.mv)} ({fmtPct(rung.r)})</div>
                        <div>{isAVMode ? fmtMoney(rung.mv) : `AV ${Math.round(rung.av).toLocaleString()}`}</div>
                        {!disabled && !isOurs && <div className="opacity-75">Click to set as our offer</div>}
                      </div>
                    }
                  >
                    {/* aria-disabled, not disabled: App.css greys out every button[disabled] with !important */}
                    <span className="absolute top-0" style={{ left, transform: 'translateX(-50%)' }}>
                      <button
                        type="button"
                        aria-disabled={disabled}
                        aria-pressed={isOurs}
                        aria-label={`${rung.label}: ${rung.hint} ${chipAmount}, ${fmtPct(rung.r)}. Set as our offer.`}
                        onClick={() => {
                          if (!disabled && !isOurs) onSelect(rung.key, rung.mv);
                        }}
                        className={`flex h-[21px] items-center gap-1 whitespace-nowrap rounded-full border px-[7px] text-[11px] leading-none tabular-nums ${disabled ? 'cursor-default' : 'hover:brightness-95'}`}
                        style={style}
                      >
                        {rung.isWalk && <LockOutlinedIcon sx={{ fontSize: 12 }} />}
                        <span className="font-bold">{rung.label}</span>
                        <span style={{ color: disabled || isOurs || rung.isWalk ? 'inherit' : t.textMuted }}>
                          {chipAmount}
                        </span>
                      </button>
                    </span>
                  </Tooltip>
                );
              })}

              <button
                type="button"
                onClick={onToggleCollapsed}
                aria-expanded
                aria-label="Collapse suggested offers"
                className={`absolute right-[-6px] top-0 flex h-[21px] w-[21px] items-center justify-center rounded ${disabled ? '' : 'hover:bg-gray-100'}`}
                style={{ color: disabled ? t.settled : t.textMuted }}
              >
                <ExpandMoreIcon sx={{ fontSize: 18 }} />
              </button>
            </div>

            {/* Track */}
            <div className="relative mt-[4px] h-[17px]">
              <div className="absolute inset-x-0 top-[5px] flex h-[7px] overflow-hidden rounded-full">
                {zones.map((zone, i) => (
                  <div key={i} style={{ width: pct(Math.max(zone.w, 0)), background: zone.color }} />
                ))}
                <div style={{ flex: 1, background: t.zonePast }} />
              </div>

              {rungs.map((rung) => (
                <div
                  key={rung.key}
                  className="absolute"
                  style={{ top: 2, left: pct(rung.p), width: 1.5, height: 13, marginLeft: -0.75, background: t.tick }}
                />
              ))}

              {town && (
                <Tooltip
                  arrow
                  placement="bottom"
                  disableInteractive
                  title={`Town offer: ${fmtMoney(town.mv)} (${fmtPct(town.r)}), ${town.pastWalk ? 'past the ceiling' : 'within the ceiling'}`}
                >
                  <div
                    className="absolute box-border border-2"
                    style={{
                      top: 3.5,
                      left: pct(town.p),
                      width: 10,
                      height: 10,
                      marginLeft: -5,
                      transform: 'rotate(45deg)',
                      background: disabled ? 'transparent' : t.surface,
                      borderColor: disabled ? t.settled : (town.pastWalk ? t.danger : t.townMarker),
                    }}
                  />
                </Tooltip>
              )}

              {our && (
                <Tooltip
                  arrow
                  placement="bottom"
                  disableInteractive
                  title={`Our offer: ${fmtMoney(our.mv)} (${fmtPct(our.r)})${outperformed ? ', ahead of the first offer' : ''}${our.pastWalk ? ', past the ceiling' : ''}`}
                >
                  <div
                    className="absolute box-border flex items-center justify-center rounded-full border-2 border-white transition-[left] duration-200 motion-reduce:transition-none"
                    style={{
                      top: 0.5,
                      left: pct(our.p),
                      width: 16,
                      height: 16,
                      marginLeft: -8,
                      background: ourDotColor,
                      boxShadow: `0 0 0 1px ${ourDotColor}`,
                    }}
                  >
                    {outperformed && <StarIcon sx={{ fontSize: 11, color: '#fff' }} />}
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
        )}
      </Popper>
    </>
  );
}
